import { useNavigate } from 'react-router-dom';
import { Briefcase, ListChecks, User } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CalendarioItemVirtual } from '@/types';
import { formatDateOnly } from '@/lib/dateOnly';

interface ItemVirtualPreviewDialogProps {
  item: CalendarioItemVirtual | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const STATUS_LABEL: Record<string, string> = {
  em_edicao: 'Em edição',
  ag_aprovacao: 'Aguardando aprovação',
  em_andamento: 'Em andamento',
  ag_validacao: 'Aguardando validação',
  finalizado: 'Finalizado',
  reprovado: 'Reprovado',
  nao_iniciado: 'Não iniciado',
  em_processo: 'Em processo',
  suspenso: 'Suspenso',
};

/**
 * Preview somente-leitura de um evento virtual (Projeto ou Atividade do ProjetoT) no Calendário.
 * Não é um evento real de `calendario_eventos` — não dá pra editar/mover por aqui, só ver um
 * resumo e navegar até o ProjetoT de verdade.
 */
export function ItemVirtualPreviewDialog({ item, open, onOpenChange }: ItemVirtualPreviewDialogProps) {
  const navigate = useNavigate();
  if (!item) return null;

  const periodo = item.data_inicio && item.data_fim && item.data_inicio !== item.data_fim
    ? `${formatDateOnly(item.data_inicio)} – ${formatDateOnly(item.data_fim)}`
    : formatDateOnly(item.data_inicio || item.data_fim);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <div className="flex items-center gap-2">
            {item.tipo === 'projeto'
              ? <Briefcase className="h-4 w-4 text-muted-foreground" />
              : <ListChecks className="h-4 w-4 text-muted-foreground" />}
            <Badge variant="secondary" className="text-xs">
              {item.tipo === 'projeto' ? 'Projeto' : 'Atividade de projeto'}
            </Badge>
          </div>
          <DialogTitle className="pt-1">{item.titulo}</DialogTitle>
          {item.tipo === 'atividade' && (
            <DialogDescription>Do projeto: {item.projeto_titulo}</DialogDescription>
          )}
        </DialogHeader>

        <div className="space-y-2 text-sm">
          {periodo && (
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Prazo</span>
              <span>{periodo}</span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Status</span>
            <span>{STATUS_LABEL[item.status] || item.status}</span>
          </div>
          {item.responsavel_nome && (
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground flex items-center gap-1">
                <User className="h-3.5 w-3.5" /> Responsável
              </span>
              <span>{item.responsavel_nome}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Fechar</Button>
          <Button onClick={() => navigate(`/projetos/${item.projeto_id}`)}>Abrir projeto</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
